import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useAppContainerContext } from '../AppContainerContext';
import { useAppContainer } from '../useAppContainer';
import { PreviousChatContext } from './DisplacedChatBanner';

type Args = {
  messageCount: number;
  clientId: string | undefined;
  clearMessages: () => void;
  clearUsedActionIds: () => void;
  cancelStreaming: () => void;
};

export function useDisplacedChat({
  messageCount,
  clientId,
  clearMessages,
  clearUsedActionIds,
  cancelStreaming,
}: Args) {
  const { contentMeta, conversationId, resetConversationId, sidebarContent, skipDefaultSidebar } =
    useAppContainerContext();
  const { setSiestaMode } = useAppContainer();
  const { flagEnabledResumeCurrentChat } = useFlags();
  const location = useLocation();
  const navigate = useNavigate();

  const chatMode = contentMeta?.chatMode;
  const entityId = contentMeta?.entityId;
  const isSiestaSidebarVisible = sidebarContent === null && !skipDefaultSidebar;

  const [previousChatContext, setPreviousChatContext] = useState<PreviousChatContext | null>(null);

  const enteredContextRef = useRef<PreviousChatContext>({
    entityId,
    chatMode,
    clientId,
    url: '',
  });

  const pendingRef = useRef({
    pathname: location.pathname,
    visible: isSiestaSidebarVisible,
    entityId,
    chatMode,
    clientId,
  });

  useEffect(() => {
    const entered = enteredContextRef.current;
    const pending = pendingRef.current;

    if (
      pending.entityId === entityId &&
      pending.chatMode === chatMode &&
      pending.clientId === clientId &&
      pending.visible &&
      isSiestaSidebarVisible
    ) {
      entered.url = pending.pathname;
    }

    pendingRef.current = {
      pathname: location.pathname,
      visible: isSiestaSidebarVisible,
      entityId,
      chatMode,
      clientId,
    };

    if (
      entered.entityId === entityId &&
      entered.chatMode === chatMode &&
      entered.clientId === clientId
    )
      return;

    const previousUrl = entered.url;
    enteredContextRef.current = { entityId, chatMode, clientId, url: '' };

    if (
      previousChatContext &&
      previousChatContext.entityId === entityId &&
      previousChatContext.chatMode === chatMode &&
      previousChatContext.clientId === clientId
    ) {
      setPreviousChatContext(null);
      return;
    }

    if (previousChatContext) return;

    if (flagEnabledResumeCurrentChat && messageCount > 0) {
      cancelStreaming();
      setPreviousChatContext({
        entityId: entered.entityId,
        chatMode: entered.chatMode,
        clientId: entered.clientId,
        url: previousUrl,
      });
      return;
    }

    clearMessages();
    resetConversationId();
    setSiestaMode('action');
    clearUsedActionIds();
  }, [
    entityId,
    chatMode,
    clientId,
    location.pathname,
    isSiestaSidebarVisible,
    previousChatContext,
    messageCount,
    flagEnabledResumeCurrentChat,
    clearMessages,
    clearUsedActionIds,
    resetConversationId,
    setSiestaMode,
    cancelStreaming,
  ]);

  const discardChat = (): string => {
    clearMessages();
    const newId = resetConversationId();
    clearUsedActionIds();
    setPreviousChatContext(null);
    return newId;
  };

  const resetIfDisplaced = (): string => {
    if (!previousChatContext) return conversationId;
    return discardChat();
  };

  const goBack = () => {
    if (previousChatContext?.url) {
      navigate(previousChatContext.url);
    }
  };

  return {
    previousChatContext,
    resetIfDisplaced,
    discardChat,
    goBack,
  };
}
